import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { pick, omitBy, isNil, isEmpty, uniqBy } from "lodash";
import { useHistory } from "react-router-dom";
import { Radio, Spin, Tooltip } from "antd";
import { Button } from "@life-line-lab/components-react";
import { SortingMode } from "../../../types/common.types";
import { toaster } from "Helpers/toaster";
import { useQuery } from "Hooks/index";
import { listLines } from "Services/delivery-settings.services";
import {
  listTrolley,
  listTrainPlan,
  createTrainPlan,
} from "Services/delivery.services";
import EnrouteTrainsList from "./enroute-tabs/trains-list";
import EnrouteTrolleysList from "./enroute-tabs/trolleys-list";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import { toNum } from "Helpers/get-to-num";

interface EnrouteTabsProps {
  loadingMechanism: any;
  queryParams: any;
  itemPerPage: any;
  setCurrentListCount: any;
}

const EnrouteTabs: React.FC<EnrouteTabsProps> = ({
  loadingMechanism,
  queryParams,
  itemPerPage,
  setCurrentListCount,
}) => {
  const { t } = useTranslation();
  const query = useQuery();
  const history = useHistory();
  const subTab = query.get("subTab");
  const [radioButtonState, setRadioButtonState] = useState(subTab ?? "0");
  const globalFilterState = useRecoilValue(GlobalFilterAtom);

  let initialFilters = {
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
  };
  const [refreshPayload, setRefreshPayload] = useState<any>(initialFilters);
  const [trainLineOptions, setTrainLineOptions] = useState<any>([]);
  const [allList, setAllList] = useState<any>([]);
  const [handoverOptions, setHandoverOptions] = useState<any>([]);
  const { isLoading } = loadingMechanism;
  const [isChildLoading, setIsChildLoading] = useState<boolean>(false);

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      onFilter({
        ancestorOrgIDs: globalFilterState?.selectedOrgIDs,
      });
    }
  }, [globalFilterState]);

  useEffect(() => {
    history.push(
      `/delivery-management/parcel-tracking?tabKey=3&subTab=${radioButtonState}`,
    );
  }, [radioButtonState]);

  const handleHandoverOptions = (data) => {
    // remove any null values
    const filteredHandover = data.filter((d) => !isEmpty(d.handoverPointData));

    const handover = uniqBy(
      filteredHandover.map((list) => list.handoverPointData),
      "id",
    );

    setHandoverOptions && setHandoverOptions(handover);
  };

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      // initialFilters status has dependency on other files, so overwrite here
      handleAPITabCalls({ ...initialFilters, status: null });
      return;
    }

    let filterValue = {};

    switch (radioButtonState) {
      case "0":
        filterValue = {
          fromDate: filterInput.fromDate || null,
          toDate: filterInput.toDate || null,
          status: filterInput.trainStatus ? [filterInput.trainStatus] : null,
          trainLine: filterInput.trainLine || null,
          orFields: filterInput.orFields || null,
        };
        break;
      case "1":
        filterValue = {
          fromDate: filterInput.fromDate || null,
          toDate: filterInput.toDate || null,
          status: filterInput.trolleyStatus
            ? [filterInput.trolleyStatus]
            : null,
          handoverPointID: filterInput.handoverPoint
            ? filterInput.handoverPoint
            : null,
          orFields: filterInput.orFields || null,
        };
        break;
      default:
    }

    setRefreshPayload(filterValue);
    handleAPITabCalls(filterValue);
  };

  const listTrainLineOptions = async () => {
    const responseData = await listLines();

    if (responseData && !responseData.error) {
      setTrainLineOptions(responseData);
    }
  };

  const handleListingTrolley = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    let filter = {
      ...payload,
      status:
        payload.status == null
          ? ["BOARDED", "RECEIVED", "UNLOADED", "DISPATCHED", "CLOSED"]
          : payload.status,
    };

    filter = pick(filter, [
      "fromDate",
      "toDate",
      "status",
      "handoverPointID",
      "orFields",
      "ancestorOrgIDs",
    ]);

    const responseData = await listTrolley(
      omitBy(filter, isNil),
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "status", mode: SortingMode.DESC },
    );

    if (responseData && !responseData.error) {
      // allow for handover point filtering if navigated to enroute/trolleys
      radioButtonState == "1" && handleHandoverOptions(responseData);
      setCurrentListCount(responseData.length);
      setAllList(responseData);
    } else {
      console.log(responseData.error.message.message);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleListingTrainPlan = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    const filter = pick(payload, [
      "fromDate",
      "toDate",
      "status",
      "trainLine",
      "orFields",
      "ancestorOrgIDs",
    ]);

    const responseData = await listTrainPlan(
      omitBy(filter, isNil),
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length);
      setAllList(responseData);
    } else {
      console.log(responseData.error.message.message);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleNewTrainPlan = async (inputData) => {
    const responseData = await createTrainPlan(inputData);

    if (responseData && !responseData.error) {
      toaster("success", "Train Plan created successfully");
      handleAPITabCalls(refreshPayload);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleAPITabCalls = (payload = {}) => {
    switch (radioButtonState) {
      case "0":
        listTrainLineOptions();
        handleListingTrainPlan(payload);
        break;
      case "1":
        handleListingTrolley(payload);
        break;
      default:
        listTrainLineOptions();
        handleListingTrainPlan(payload);
        break;
    }
  };

  const tabs = [
    {
      key: "0",
      component: (
        <EnrouteTrainsList
          onFilter={onFilter}
          trainList={allList}
          trainLineOptions={trainLineOptions}
          onSubmitNewTrainPlan={handleNewTrainPlan}
        />
      ),
    },
    {
      key: "1",
      component: (
        <EnrouteTrolleysList
          trolleyList={allList}
          handoverOptions={handoverOptions}
          onFilter={onFilter}
        />
      ),
    },
  ];

  return (
    <div className="parcel-order-list">
      <div>
        <Radio.Group
          className="m-20 mb-10"
          defaultValue="0"
          value={radioButtonState}
          buttonStyle="solid"
          onChange={(event) => {
            setRadioButtonState(event.target.value);
          }}
        >
          <Radio.Button value="0">{t("option.trains")}</Radio.Button>
          <Radio.Button value="1">{t("option.trolleys")}</Radio.Button>
        </Radio.Group>
        <Tooltip title="Refresh" placement="right">
          <Button
            className="op-refresh-btn"
            icon={isChildLoading ? <LoadingOutlined /> : <ReloadOutlined />}
            isDisabled={isChildLoading}
            onClick={() => handleAPITabCalls(refreshPayload)}
            shape="circle"
          />
        </Tooltip>
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <div>{tabs[radioButtonState].component}</div>
      )}
    </div>
  );
};

export default EnrouteTabs;
